import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../api";
import { useLanguage } from "../i18n";

export default function PaymentSuccess() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("processing");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const res = await api.get(`/orders/by-session/${sessionId}`);
          if (!cancelled) {
            const o = res.data.data;
            setOrder(o);
            setState(o && o.status && o.status !== "completed" ? "provision_failed" : "paid");
            // la Console account auto-aggiorna per qualche minuto: la SIM appare da sola
            try { sessionStorage.setItem("atlas_just_bought", String(Date.now())); } catch { /* storage non disponibile */ }
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (!cancelled && attempts.current < 20) {
        setTimeout(poll, 2000);
      } else if (!cancelled) {
        setState("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-16 sm:py-24">
      {state === "processing" && (
        <div data-testid="payment-processing" className="text-center py-24">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-[var(--accent-terracotta)]" />
          <p className="mt-6 font-mono-t text-xs uppercase tracking-[0.3em] text-[var(--text-muted)]">{t("pay.processing")}</p>
        </div>
      )}

      {state === "failed" && (
        <div data-testid="payment-failed" className="text-center py-24">
          <XCircle className="w-12 h-12 mx-auto text-[var(--accent-terracotta)]" />
          <h1 className="font-display font-extrabold text-4xl mt-6">{t("pay.fail")}</h1>
          <Link to="/destinations" data-testid="payment-fail-back" className="inline-block mt-8 rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-8 py-3 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
            {t("pay.continue")}
          </Link>
        </div>
      )}

      {state === "provision_failed" && (
        <div data-testid="payment-provision-failed" className="text-center py-24">
          <XCircle className="w-12 h-12 mx-auto text-[var(--accent-terracotta)]" />
          <h1 className="font-display font-extrabold text-4xl mt-6">{t("pay.provFail")}</h1>
          <p className="mt-4 text-[var(--text-secondary)] max-w-md mx-auto">
            {order?.refunded ? t("pay.provFailRefunded") : t("pay.provFailContact")}
          </p>
          {order?.code && (
            <div className="mt-4 font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
              {t("acc.order")} {order.code}
            </div>
          )}
          <Link to="/account" data-testid="payment-provfail-account" className="inline-block mt-8 rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-8 py-3 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
            {t("pay.viewEsims")}
          </Link>
        </div>
      )}

      {state === "paid" && order && (
        <div>
          <div className="text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[var(--status-active-green)]" />
            <h1 data-testid="payment-success-title" className="font-display font-black text-4xl sm:text-6xl tracking-tight mt-6">
              {order.kind === "topup" ? t("pay.topupSuccess") : t("pay.success")}
            </h1>
            <p className="mt-4 text-[var(--text-secondary)] max-w-md mx-auto">
              {order.kind === "topup" ? t("pay.topupSub") : t("pay.successSub")}
            </p>
            {order.mock && (
              <div data-testid="order-demo-badge" className="mt-4 inline-block rounded-full border border-[var(--accent-gold)] px-4 py-1.5 font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--accent-gold)]">
                {t("acc.demo")} — {order.code}
              </div>
            )}
            {!order.mock && order.code && (
              <div className="mt-4 inline-block font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
                {t("acc.order")} {order.code}
              </div>
            )}
          </div>

          {order.kind !== "topup" && (order.sims || []).length > 0 && (
            <div className="mt-14 grid gap-6 sm:grid-cols-2">
              {order.sims.map((sim) => (
                <div key={sim.iccid} data-testid={`esim-qr-code-${sim.iccid}`} className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-8 text-center">
                  <div className="font-display font-bold text-xl">{order.package_title}</div>
                  <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)] mt-1">{order.destination}</div>
                  {sim.qrcode && (
                    <div className="mt-6 inline-block rounded-2xl border border-[var(--border-strong)] p-4 bg-white">
                      <QRCodeSVG value={sim.qrcode} size={180} />
                    </div>
                  )}
                  <div className="mt-6 text-left space-y-3">
                    <div>
                      <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">{t("acc.smdp")}</div>
                      <div className="font-mono-t text-sm break-all mt-1">{sim.lpa}</div>
                    </div>
                    <div>
                      <div className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">{t("acc.code")}</div>
                      <div className="font-mono-t text-sm break-all mt-1">{sim.matching_id}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="mt-10 text-center text-sm text-[var(--text-muted)]">{t("pay.keep")}</p>
          <p data-testid="payment-email-note" className="mt-2 text-center text-sm text-[var(--text-muted)]">{t("pay.emailSent")}</p>
          <div className="mt-6 text-center">
            <Link to="/account" data-testid="payment-view-esims" className="inline-block rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-8 py-3 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[var(--accent-terracotta)] transition-colors">
              {t("pay.viewEsims")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
